import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, Clock, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { NewsletterForm } from '../sections/NewsletterForm';
import { APP_VERSION } from '../../version';
import logoImg from '../../assets/logo-96.png';

export const Footer = () => {
  const { language, t } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0D2440] text-white pb-24 sm:pb-0" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Newsletter Section */}
        <div className="py-16 border-b border-white/10">
          <NewsletterForm />
        </div>

        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {/* Company Info */}
          <div className="md:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <img 
                src={logoImg}
                alt="e-Matrix"
                className="w-12 h-12 rounded-full bg-white object-cover"
                width="48"
                height="48"
              />
              <span className="font-semibold text-xl">e-Matrix Innovations GmbH</span>
            </div>
            <p className="text-white/70 max-w-md mb-6">
              {language === 'de' 
                ? 'Corporate E-Learning - wir schaffen Bewusstsein' 
                : 'Corporate E-Learning - we create awareness'}
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.facebook.com/eMatrixInnovations"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                data-testid="social-facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/e-matrix-innovations"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                data-testid="social-linkedin"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Links</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/solutions"
                  className="text-white/70 hover:text-white transition-colors flex items-center group"
                >
                  {language === 'de' ? 'Lösungen' : 'Solutions'}
                  <ArrowUpRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link
                  to="/produktkatalog"
                  className="text-white/70 hover:text-white transition-colors flex items-center group"
                >
                  {language === 'de' ? 'Produktkatalog' : 'Product Catalog'}
                  <ArrowUpRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link
                  to="/portfolio"
                  className="text-white/70 hover:text-white transition-colors flex items-center group"
                >
                  Portfolio
                  <ArrowUpRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link
                  to="/partner"
                  className="text-white/70 hover:text-white transition-colors flex items-center group"
                >
                  Partner
                  <ArrowUpRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link
                  to="/downloads"
                  className="text-white/70 hover:text-white transition-colors flex items-center group"
                >
                  Downloads
                  <ArrowUpRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="text-white/70 hover:text-white transition-colors flex items-center group"
                >
                  {language === 'de' ? 'Über uns' : 'About Us'}
                  <ArrowUpRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-white/70 hover:text-white transition-colors flex items-center group"
                >
                  {language === 'de' ? 'Kontakt' : 'Contact'}
                  <ArrowUpRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link
                  to="/faq"
                  className="text-white/70 hover:text-white transition-colors flex items-center group"
                >
                  FAQ
                  <ArrowUpRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info - Real e-Matrix Data */}
          <div>
            <h4 className="font-semibold text-lg mb-6">
              {language === 'de' ? 'Kontakt' : 'Contact'}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-[#2E5E99] mt-0.5 flex-shrink-0" />
                <a href="tel:+4372429328080" className="text-white/70 hover:text-white transition-colors">
                  +43 7242 932808
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-[#2E5E99] mt-0.5 flex-shrink-0" />
                <span className="text-white/70 text-sm">
                  {language === 'de' 
                    ? 'Mo. - Do. 8:00 - 17:00\nFr. 8:00 - 13:00' 
                    : 'Mon - Thu 8:00 - 17:00\nFri 8:00 - 13:00'}
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[#2E5E99] mt-0.5 flex-shrink-0" />
                <a 
                  href="https://maps.app.goo.gl/r6TioCKDChTmtFAKA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-white transition-colors"
                >
                  Stelzhamerstr. 9<br />
                  4600 Wels<br />
                  {language === 'de' ? 'Österreich' : 'Austria'}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-[#2E5E99] mt-0.5 flex-shrink-0" />
                <div className="text-white/70">
                  <a href="mailto:office@e-matrix.at" className="hover:text-white transition-colors block">
                    office@e-matrix.at
                  </a>
                  <a href="mailto:e-learning@e-matrix.at" className="hover:text-white transition-colors block text-sm mt-1">
                    e-learning@e-matrix.at
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-8 border-t border-white/10 flex flex-col items-center space-y-4 sm:flex-row sm:justify-between sm:space-y-0">
          <p className="text-white/50 text-sm text-center sm:text-left">
            © {currentYear} e-Matrix Innovations GmbH. {language === 'de' ? 'Alle Rechte vorbehalten.' : 'All rights reserved.'}
          </p>
          <div className="flex items-center space-x-3 text-sm sm:mr-48">
            <Link to={language === 'de' ? '/datenschutz' : '/privacy-policy'} className="text-white/50 hover:text-white transition-colors" data-testid="footer-privacy">
              {language === 'de' ? 'Datenschutz' : 'Privacy Policy'}
            </Link>
            <span className="text-white/30">|</span>
            <Link to={language === 'de' ? '/impressum' : '/imprint'} className="text-white/50 hover:text-white transition-colors" data-testid="footer-imprint">
              {language === 'de' ? 'Impressum' : 'Imprint'}
            </Link>
            <span className="text-white/30">|</span>
            <span className="text-white/30 text-xs" data-testid="footer-version">{APP_VERSION}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
